"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AppointmentOpenApiOperations = void 0;
const errors_1 = require("../errors");
function errorCodesFor(endpoint) {
    return errors_1.AppointmentApiErrorRegistry[endpoint]?.errors.map((entry) => entry.code) ?? [];
}
/**
 * One entry per endpoint contract this stage defines — 35 operations
 * across the 8 categories named in the brief. `path` is illustrative
 * REST shape for when a controller layer is eventually built (Stage
 * 5B.5 builds no routes); Stage 5B.4's Application Services already
 * name the exact orchestration each of these would call.
 */
exports.AppointmentOpenApiOperations = [
    // Booking
    {
        operationId: 'createAppointment',
        method: 'POST',
        path: '/v1/appointments',
        summary: 'Book a standard, scheduled appointment',
        version: 'v1',
        tags: ['Booking'],
        requestSchemaRef: 'CreateAppointmentRequest',
        responseSchemaRef: 'AppointmentBookingResponseDto',
        errorCodes: errorCodesFor('createAppointment'),
    },
    {
        operationId: 'walkInAppointment',
        method: 'POST',
        path: '/v1/appointments/walk-in',
        summary: 'Register a walk-in arrival with no prior appointment',
        version: 'v1',
        tags: ['Booking'],
        requestSchemaRef: 'WalkInAppointmentRequest',
        responseSchemaRef: 'ImmediateIntakeBookingResponseDto',
        errorCodes: errorCodesFor('walkInAppointment'),
    },
    {
        operationId: 'emergencyAppointment',
        method: 'POST',
        path: '/v1/appointments/emergency',
        summary: 'Register an emergency intake with no prior appointment',
        version: 'v1',
        tags: ['Booking'],
        requestSchemaRef: 'EmergencyAppointmentRequest',
        responseSchemaRef: 'ImmediateIntakeBookingResponseDto',
        errorCodes: errorCodesFor('emergencyAppointment'),
    },
    {
        operationId: 'corporateAppointment',
        method: 'POST',
        path: '/v1/appointments/corporate',
        summary: 'Book an appointment against a corporate tariff',
        version: 'v1',
        tags: ['Booking'],
        requestSchemaRef: 'CorporateAppointmentRequest',
        responseSchemaRef: 'AppointmentBookingResponseDto',
        errorCodes: errorCodesFor('corporateAppointment'),
    },
    {
        operationId: 'referralAppointment',
        method: 'POST',
        path: '/v1/appointments/referral',
        summary: 'Book an appointment from a doctor-to-doctor referral',
        version: 'v1',
        tags: ['Booking'],
        requestSchemaRef: 'ReferralAppointmentRequest',
        responseSchemaRef: 'AppointmentBookingResponseDto',
        errorCodes: errorCodesFor('referralAppointment'),
    },
    {
        operationId: 'telemedicineAppointment',
        method: 'POST',
        path: '/v1/appointments/telemedicine',
        summary: 'Book a telemedicine (video) consultation',
        version: 'v1',
        tags: ['Booking'],
        requestSchemaRef: 'TelemedicineAppointmentRequest',
        responseSchemaRef: 'AppointmentBookingResponseDto',
        errorCodes: errorCodesFor('telemedicineAppointment'),
    },
    {
        operationId: 'healthCheckAppointment',
        method: 'POST',
        path: '/v1/appointments/health-check',
        summary: 'Book a preventive health-check appointment',
        version: 'v1',
        tags: ['Booking'],
        requestSchemaRef: 'HealthCheckAppointmentRequest',
        responseSchemaRef: 'AppointmentBookingResponseDto',
        errorCodes: errorCodesFor('healthCheckAppointment'),
    },
    {
        operationId: 'packageAppointment',
        method: 'POST',
        path: '/v1/appointments/package',
        summary: 'Book an appointment against a pre-purchased package',
        version: 'v1',
        tags: ['Booking'],
        requestSchemaRef: 'PackageAppointmentRequest',
        responseSchemaRef: 'AppointmentBookingResponseDto',
        errorCodes: errorCodesFor('packageAppointment'),
    },
    // Scheduling
    {
        operationId: 'rescheduleAppointment',
        method: 'POST',
        path: '/v1/appointments/{appointmentId}/reschedule',
        summary: 'Move an appointment to a new slot as one logical operation',
        version: 'v1',
        tags: ['Scheduling'],
        requestSchemaRef: 'RescheduleAppointmentRequest',
        responseSchemaRef: 'AppointmentDto',
        errorCodes: errorCodesFor('rescheduleAppointment'),
    },
    {
        operationId: 'cancelAppointment',
        method: 'POST',
        path: '/v1/appointments/{appointmentId}/cancel',
        summary: 'Cancel an appointment',
        version: 'v1',
        tags: ['Scheduling'],
        requestSchemaRef: 'CancelAppointmentRequest',
        responseSchemaRef: 'CancelAppointmentResponseDto',
        errorCodes: errorCodesFor('cancelAppointment'),
    },
    {
        operationId: 'confirmAppointment',
        method: 'POST',
        path: '/v1/appointments/{appointmentId}/confirm',
        summary: 'Confirm a pending appointment',
        version: 'v1',
        tags: ['Scheduling'],
        requestSchemaRef: 'ConfirmAppointmentRequest',
        responseSchemaRef: 'AppointmentDto',
        errorCodes: errorCodesFor('confirmAppointment'),
    },
    {
        operationId: 'appointmentExpiry',
        method: 'POST',
        path: '/v1/appointments/expiry-sweep',
        summary: 'Expire appointments still pending confirmation past their scheduled time',
        version: 'v1',
        tags: ['Scheduling'],
        requestSchemaRef: 'AppointmentExpiryRequest',
        responseSchemaRef: 'AppointmentExpiryResponseDto',
        errorCodes: errorCodesFor('appointmentExpiry'),
    },
    // Check-In
    {
        operationId: 'patientArrival',
        method: 'POST',
        path: '/v1/check-in/arrival',
        summary: 'Register a patient arrival with no prior appointment',
        version: 'v1',
        tags: ['CheckIn'],
        requestSchemaRef: 'PatientArrivalRequest',
        responseSchemaRef: 'PatientArrivalResponseDto',
        errorCodes: errorCodesFor('patientArrival'),
    },
    {
        operationId: 'patientCheckIn',
        method: 'POST',
        path: '/v1/appointments/{appointmentId}/check-in',
        summary: 'Formally check in a confirmed appointment',
        version: 'v1',
        tags: ['CheckIn'],
        requestSchemaRef: 'PatientCheckInRequest',
        responseSchemaRef: 'PatientCheckInResponseDto',
        errorCodes: errorCodesFor('patientCheckIn'),
    },
    {
        operationId: 'lateArrival',
        method: 'POST',
        path: '/v1/appointments/{appointmentId}/late-arrival',
        summary: 'Check in a patient arriving past their scheduled time',
        version: 'v1',
        tags: ['CheckIn'],
        requestSchemaRef: 'LateArrivalRequest',
        responseSchemaRef: 'LateArrivalResponseDto',
        errorCodes: errorCodesFor('lateArrival'),
    },
    {
        operationId: 'noShow',
        method: 'POST',
        path: '/v1/appointments/{appointmentId}/no-show',
        summary: 'Mark a confirmed appointment as a no-show',
        version: 'v1',
        tags: ['CheckIn'],
        requestSchemaRef: 'NoShowRequest',
        responseSchemaRef: 'AppointmentDto',
        errorCodes: errorCodesFor('noShow'),
    },
    {
        operationId: 'completeAppointment',
        method: 'POST',
        path: '/v1/appointments/{appointmentId}/complete',
        summary: 'Mark a checked-in appointment as completed',
        version: 'v1',
        tags: ['CheckIn'],
        requestSchemaRef: 'CompleteAppointmentRequest',
        responseSchemaRef: 'AppointmentDto',
        errorCodes: errorCodesFor('completeAppointment'),
    },
    // Queue
    /*
      P0-3 — the queue READ, which this module's own route file recorded as
      technical debt. `requestSchemaRef` names the query schema: the versioning
      test requires a non-empty ref on every operation, and a GET's query
      parameters are a request contract like any other.
    */
    {
        operationId: 'listQueue',
        method: 'GET',
        path: '/v1/queue-entries',
        summary: 'List the live queue — who is waiting, called or in consultation',
        version: 'v1',
        tags: ['Queue'],
        requestSchemaRef: 'ListQueueQuery',
        responseSchemaRef: 'QueueEntryDto',
        errorCodes: errorCodesFor('listQueue'),
    },
    {
        operationId: 'generateQueue',
        method: 'POST',
        path: '/v1/queue-entries',
        summary: 'Add a queue entry for an existing visit',
        version: 'v1',
        tags: ['Queue'],
        requestSchemaRef: 'GenerateQueueRequest',
        responseSchemaRef: 'AppointmentQueueDto',
        errorCodes: errorCodesFor('generateQueue'),
    },
    {
        operationId: 'generateToken',
        method: 'POST',
        path: '/v1/queue-tokens',
        summary: 'Issue a queue token for a visit',
        version: 'v1',
        tags: ['Queue'],
        requestSchemaRef: 'GenerateTokenRequest',
        responseSchemaRef: 'QueueTokenDto',
        errorCodes: errorCodesFor('generateToken'),
    },
    {
        operationId: 'moveQueue',
        method: 'POST',
        path: '/v1/queue-entries/{queueEntryId}/transfer',
        summary: "Transfer a queue entry to a different doctor's queue",
        version: 'v1',
        tags: ['Queue'],
        requestSchemaRef: 'MoveQueueRequest',
        responseSchemaRef: 'AppointmentQueueDto',
        errorCodes: errorCodesFor('moveQueue'),
    },
    {
        operationId: 'recallPatient',
        method: 'POST',
        path: '/v1/queue-entries/{queueEntryId}/recall',
        summary: 'Recall a previously skipped queue entry',
        version: 'v1',
        tags: ['Queue'],
        requestSchemaRef: 'RecallPatientRequest',
        responseSchemaRef: 'AppointmentQueueDto',
        errorCodes: errorCodesFor('recallPatient'),
    },
    {
        operationId: 'skipPatient',
        method: 'POST',
        path: '/v1/queue-entries/{queueEntryId}/skip',
        summary: 'Skip a called-but-absent queue entry',
        version: 'v1',
        tags: ['Queue'],
        requestSchemaRef: 'SkipPatientRequest',
        responseSchemaRef: 'AppointmentQueueDto',
        errorCodes: errorCodesFor('skipPatient'),
    },
    {
        operationId: 'priorityQueue',
        method: 'POST',
        path: '/v1/queue/resolve-priority',
        summary: 'Resolve the priority tier for a given clinical/VIP/emergency context',
        version: 'v1',
        tags: ['Queue'],
        requestSchemaRef: 'PriorityQueueRequest',
        responseSchemaRef: 'PriorityQueueResponseDto',
        errorCodes: errorCodesFor('priorityQueue'),
    },
    // Waitlist
    {
        operationId: 'joinWaitlist',
        method: 'POST',
        path: '/v1/waitlist-entries',
        summary: 'Register a patient on the waitlist for a doctor/department',
        version: 'v1',
        tags: ['Waitlist'],
        requestSchemaRef: 'JoinWaitlistRequest',
        responseSchemaRef: 'AppointmentWaitlistDto',
        errorCodes: errorCodesFor('joinWaitlist'),
    },
    {
        operationId: 'promoteWaitlist',
        method: 'POST',
        path: '/v1/waitlist-entries/promote',
        summary: 'Offer a freed slot to the next-in-line waitlist entry and book it',
        version: 'v1',
        tags: ['Waitlist'],
        requestSchemaRef: 'PromoteWaitlistRequest',
        responseSchemaRef: 'PromoteWaitlistResponseDto',
        errorCodes: errorCodesFor('promoteWaitlist'),
    },
    {
        operationId: 'expireWaitlist',
        method: 'POST',
        path: '/v1/waitlist-entries/expiry-sweep',
        summary: 'Expire waitlist offers nobody accepted in time',
        version: 'v1',
        tags: ['Waitlist'],
        requestSchemaRef: 'ExpireWaitlistRequest',
        responseSchemaRef: 'ExpireWaitlistResponseDto',
        errorCodes: errorCodesFor('expireWaitlist'),
    },
    // Slot Management
    {
        operationId: 'getAvailableSlots',
        method: 'POST',
        path: '/v1/slots/search',
        summary: "Compute a doctor's bookable slots over a date range",
        version: 'v1',
        tags: ['SlotManagement'],
        requestSchemaRef: 'GetAvailableSlotsRequest',
        responseSchemaRef: 'GetAvailableSlotsResponseDto',
        errorCodes: errorCodesFor('getAvailableSlots'),
    },
    {
        operationId: 'holdSlot',
        method: 'POST',
        path: '/v1/slot-holds',
        summary: 'Acquire a temporary hold on a slot',
        version: 'v1',
        tags: ['SlotManagement'],
        requestSchemaRef: 'HoldSlotRequest',
        responseSchemaRef: 'AppointmentSlotHoldDto',
        errorCodes: errorCodesFor('holdSlot'),
    },
    {
        operationId: 'releaseSlot',
        method: 'POST',
        path: '/v1/slot-holds/{holdId}/release',
        summary: 'Release a previously acquired slot hold',
        version: 'v1',
        tags: ['SlotManagement'],
        requestSchemaRef: 'ReleaseSlotRequest',
        responseSchemaRef: 'ReleaseSlotResponseDto',
        errorCodes: errorCodesFor('releaseSlot'),
    },
    // Doctor Availability
    {
        operationId: 'createSchedule',
        method: 'POST',
        path: '/v1/doctor-schedules',
        summary: "Create a doctor's weekly schedule and its sessions",
        version: 'v1',
        tags: ['DoctorAvailability'],
        requestSchemaRef: 'CreateScheduleRequest',
        responseSchemaRef: 'DoctorScheduleDto',
        errorCodes: errorCodesFor('createSchedule'),
    },
    {
        operationId: 'updateSchedule',
        method: 'PATCH',
        path: '/v1/doctor-schedules/{scheduleId}',
        summary: "Update a doctor schedule header's mutable fields",
        version: 'v1',
        tags: ['DoctorAvailability'],
        requestSchemaRef: 'UpdateScheduleRequest',
        responseSchemaRef: 'DoctorScheduleDto',
        errorCodes: errorCodesFor('updateSchedule'),
    },
    {
        operationId: 'doctorLeave',
        method: 'POST',
        path: '/v1/doctor-leaves',
        summary: "Request a doctor's leave, routed through the approval matrix if configured",
        version: 'v1',
        tags: ['DoctorAvailability'],
        requestSchemaRef: 'DoctorLeaveRequest',
        responseSchemaRef: 'RequestLeaveResponseDto',
        errorCodes: errorCodesFor('doctorLeave'),
    },
    {
        operationId: 'availabilityOverride',
        method: 'POST',
        path: '/v1/doctor-availability-overrides',
        summary: "Set an ad hoc block or extra-availability window on a doctor's calendar",
        version: 'v1',
        tags: ['DoctorAvailability'],
        requestSchemaRef: 'AvailabilityOverrideRequest',
        responseSchemaRef: 'DoctorAvailabilityOverrideDto',
        errorCodes: errorCodesFor('availabilityOverride'),
    },
    // Reminder
    {
        operationId: 'scheduleReminder',
        method: 'POST',
        path: '/v1/appointment-reminders',
        summary: 'Schedule a future reminder for an appointment',
        version: 'v1',
        tags: ['Reminder'],
        requestSchemaRef: 'ScheduleReminderRequest',
        responseSchemaRef: 'AppointmentReminderDto',
        errorCodes: errorCodesFor('scheduleReminder'),
    },
    {
        operationId: 'cancelReminder',
        method: 'POST',
        path: '/v1/appointment-reminders/{reminderId}/cancel',
        summary: 'Cancel a not-yet-sent reminder',
        version: 'v1',
        tags: ['Reminder'],
        requestSchemaRef: 'CancelReminderRequest',
        responseSchemaRef: 'AppointmentReminderDto',
        errorCodes: errorCodesFor('cancelReminder'),
    },
];
//# sourceMappingURL=appointment.operations.js.map